import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy, ExternalLink } from 'lucide-react';
import api from '../../services/api';
import { PageSeoEditor } from './PageSeoEditor';

const SITE = 'https://vyomos.io';

const copy = (text) => navigator.clipboard.writeText(text).then(() => toast.success('Copied'), () => toast.error('Copy failed'));

function FileCard({ name, body }) {
  const url = `${SITE}/${name}`;
  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-white" data-testid={`search-file-${name}`}>
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <code className="text-sm font-semibold text-slate-900">{url}</code>
        <div className="flex gap-2">
          <button type="button" onClick={() => copy(url)} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50" data-testid={`search-copy-url-${name}`}><Copy className="w-3 h-3" /> Copy link</button>
          <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50" data-testid={`search-open-${name}`}><ExternalLink className="w-3 h-3" /> Open</a>
        </div>
      </div>
      <pre className="text-xs bg-slate-50 border border-slate-200 rounded p-3 overflow-x-auto max-h-64 whitespace-pre-wrap" data-testid={`search-file-body-${name}`}>{body ?? 'Loading…'}</pre>
    </div>
  );
}

function PageSeoList({ pages, reload }) {
  const [open, setOpen] = useState(null);
  return (
    <div className="space-y-2" data-testid="search-page-list">
      {pages.map((p) => {
        const indexed = p.status === 'published' && !p.noindex;
        return (
          <div key={p.id} className="border border-slate-200 rounded-lg px-3 py-2 bg-white" data-testid={`search-page-${p.slug}`}>
            <button type="button" onClick={() => setOpen(open === p.id ? null : p.id)} className="w-full flex items-center gap-3 text-left" data-testid={`search-page-toggle-${p.slug}`}>
              <span className="font-medium text-sm text-slate-900 w-40 truncate">{p.title}</span>
              <code className="text-xs text-slate-400 w-32 truncate">/{p.slug === 'home' ? '' : p.slug}</code>
              <span className="flex-1 text-xs text-slate-500 truncate">{p.meta_title || <em className="text-amber-600">No meta title yet</em>}</span>
              <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${indexed ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{indexed ? 'Indexed' : 'Hidden'}</span>
            </button>
            {open === p.id && <PageSeoEditor page={p} onSaved={reload} />}
          </div>
        );
      })}
    </div>
  );
}

export default function WebsiteSearchPanel() {
  const [pages, setPages] = useState([]);
  const [files, setFiles] = useState({});
  const load = () => api.get('/super-admin/website/pages').then(({ data }) => setPages(data.pages || [])).catch(() => toast.error('Failed to load pages'));
  useEffect(() => {
    load();
    ['robots.txt', 'sitemap.xml'].forEach((n) =>
      fetch(`/${n}`, { cache: 'no-store' }).then((r) => r.text()).then((t) => setFiles((f) => ({ ...f, [n]: t }))).catch(() => setFiles((f) => ({ ...f, [n]: 'Could not load this file.' }))));
  }, []);
  return (
    <div className="max-w-5xl space-y-8" data-testid="website-search-panel">
      <section className="space-y-3">
        <h2 className="text-base font-semibold text-slate-900">Google Search Console</h2>
        <p className="text-sm text-slate-600">In Search Console, open <strong>Sitemaps</strong> and submit <code className="px-1 bg-slate-100 rounded">sitemap.xml</code> (full link: <code className="px-1 bg-slate-100 rounded">{SITE}/sitemap.xml</code>). Both files are live at the links below.</p>
        <div className="grid lg:grid-cols-2 gap-4">
          <FileCard name="sitemap.xml" body={files['sitemap.xml']} />
          <FileCard name="robots.txt" body={files['robots.txt']} />
        </div>
      </section>
      <section className="space-y-3">
        <h2 className="text-base font-semibold text-slate-900">Page titles, descriptions & indexing</h2>
        <p className="text-sm text-slate-600">Click a page to edit its meta title, meta description, canonical URL and whether search engines may index it.</p>
        <PageSeoList pages={pages} reload={load} />
      </section>
    </div>
  );
}
